import { useCallback, useEffect, useMemo, useState } from 'react';
import Button from '@/components/base/Button';
import StaffNotation from '@/components/feature/StaffNotation';
import PianoKeyboard from '@/components/feature/PianoKeyboard';
import { playMidi, resumeAudio } from '@/lib/audio/synth';
import { getNote } from '@/lib/music/notes';

type Order = 'ascending' | 'descending' | 'random';
type Advance = 'automatic' | 'manual';
type Reference = 'after' | 'before' | 'off';
interface InstrumentPracticeProps {
  noteIds: string[];
  lessonTitle: string;
  onClose: () => void;
}
const LETTERS = ['C', 'D', 'E', 'F', 'G', 'A', 'B'];
const fieldClass = 'mt-1 w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900';
const sortNotes = (ids: string[]) => [...new Set(ids)].sort((a, b) => getNote(a).midi - getNote(b).midi);

/** Prática autônoma: não recebe sinal do instrumento nem registra respostas avaliadas. */
export default function InstrumentPractice({ noteIds, lessonTitle, onClose }: InstrumentPracticeProps) {
  const lessonNotes = useMemo(() => sortNotes(noteIds), [noteIds]);
  const [region, setRegion] = useState('lesson');
  const [selected, setSelected] = useState(lessonNotes);
  const [current, setCurrent] = useState(lessonNotes[0]);
  const [order, setOrder] = useState<Order>('ascending');
  const [advance, setAdvance] = useState<Advance>('automatic');
  const [seconds, setSeconds] = useState(4);
  const [reference, setReference] = useState<Reference>('after');
  const [volume, setVolume] = useState(20);
  const [showNames, setShowNames] = useState(true);
  const [showKeyboard, setShowKeyboard] = useState(false);
  const [revealed, setRevealed] = useState(false);
  const [running, setRunning] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [presented, setPresented] = useState(1);
  const available = useMemo(
    () => region === 'lesson' ? lessonNotes : LETTERS.map(letter => letter + region),
    [lessonNotes, region],
  );
  const note = getNote(current);
  const keyboardStart = Math.floor(getNote(available[0]).midi / 12) * 12;
  const keyboardEnd = Math.floor(getNote(available[available.length - 1]).midi / 12) * 12 + 11;

  const reset = (ids: string[], nextOrder: Order = order) => {
    setRunning(false);
    setElapsed(0);
    setRevealed(false);
    setSelected(ids);
    setCurrent(nextOrder === 'descending' ? ids[ids.length - 1] : ids[0]);
    setPresented(1);
  };
  const pause = () => setRunning(false);
  const hear = useCallback(() => {
    resumeAudio();
    playMidi(getNote(current).midi, 0.8, volume / 100);
    setRevealed(true);
  }, [current, volume]);
  const nextNote = useCallback(() => {
    setCurrent(previous => {
      if (selected.length === 1) return selected[0];
      if (order === 'random') {
        const choices = selected.filter(id => id !== previous);
        return choices[Math.floor(Math.random() * choices.length)];
      }
      const direction = order === 'descending' ? -1 : 1;
      const index = selected.indexOf(previous);
      return selected[(index + direction + selected.length) % selected.length];
    });
    setPresented(value => value + 1);
    setElapsed(0);
    setRevealed(false);
  }, [order, selected]);
  const toggleRunning = useCallback(() => {
    resumeAudio();
    setElapsed(0);
    setRevealed(false);
    setRunning(value => !value);
  }, []);

  useEffect(() => {
    if (!running || advance !== 'automatic') return undefined;
    const started = performance.now();
    let played = false;
    if (reference === 'before') {
      playMidi(getNote(current).midi, 0.8, volume / 100);
      played = true;
    }
    const timer = window.setInterval(() => {
      const position = (performance.now() - started) / (seconds * 1000);
      setElapsed(Math.min(position, 1));
      if (reference === 'after' && !played && position >= 0.6) {
        played = true;
        playMidi(getNote(current).midi, Math.min(0.8, seconds * 0.3), volume / 100);
        setRevealed(true);
      }
      if (position >= 1) {
        window.clearInterval(timer);
        nextNote();
      }
    }, 80);
    return () => window.clearInterval(timer);
  }, [advance, current, nextNote, presented, reference, running, seconds, volume]);

  useEffect(() => {
    const onVisibility = () => { if (document.hidden) setRunning(false); };
    const onKey = (event: KeyboardEvent) => {
      const target = event.target;
      if (event.repeat || (target instanceof HTMLElement && target.closest('input, select, textarea, button, a, [contenteditable="true"]'))) return;
      if (event.code === 'Space' && advance === 'automatic') {
        event.preventDefault();
        toggleRunning();
      } else if (event.code === 'ArrowRight') {
        event.preventDefault();
        nextNote();
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('keydown', onKey);
    };
  }, [advance, nextNote, toggleRunning]);

  const phase = !running
    ? advance === 'manual' ? 'Toque e avance no seu ritmo' : 'Pronto para tocar'
    : reference === 'after' && elapsed >= 0.6 ? 'Ouça e confira' : 'Leia a pauta e toque';
  return (
    <section className="mx-auto max-w-5xl px-3 py-6 sm:px-4 sm:py-8">
      <Button variant="ghost" size="sm" icon="ri-arrow-left-line" onClick={onClose}>Voltar à aula</Button>
      <header className="mt-4">
        <p className="text-sm text-foreground-500">{lessonTitle}</p>
        <h1 className="mt-1 font-heading text-2xl font-extrabold text-foreground-950 sm:text-3xl">Treino no meu piano</h1>
        <p className="mt-2 max-w-3xl text-sm text-foreground-700">
          Coloque o celular ou computador ao lado do instrumento. Veja a nota, encontre a tecla no seu piano e toque. Depois, confira pelo nome, pelo teclado de apoio ou pelo som.
        </p>
      </header>

      <div className="mt-5 rounded-xl border border-primary-200 bg-background-50 p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <span className="font-semibold text-primary-700">{phase}</span>
          <span className="text-foreground-500" data-testid="practice-counter">Notas apresentadas: {presented}</span>
        </div>
        <div className="mx-auto my-4 w-full max-w-sm" data-testid="practice-note" data-note-id={current}>
          <StaffNotation notes={[current]} />
        </div>
        <div className="min-h-12 text-center">
          {showNames || revealed ? (
            <p className="font-heading text-2xl font-bold text-foreground-950" data-testid="practice-answer">
              {note.ptName} <span className="text-base font-normal text-foreground-500">· {note.octave}ª oitava</span>
            </p>
          ) : <p className="text-sm text-foreground-500">Encontre a nota no instrumento antes de conferir.</p>}
        </div>
        {advance === 'automatic' ? (
          <div className="mt-4">
            <div className="h-1.5 overflow-hidden rounded-full bg-background-200" role="progressbar" aria-label="Tempo da nota" aria-valuenow={Math.round(elapsed * 100)} aria-valuemin={0} aria-valuemax={100}>
              <div className="h-full bg-primary-500 transition-[width] duration-75" style={{width: (elapsed * 100) + '%'}} />
            </div>
            <p className="mt-1 text-center text-xs text-foreground-500">
              {running ? 'Próxima nota em ' + Math.max(1, Math.ceil(seconds * (1 - elapsed))) + ' s' : seconds + ' segundos por nota · Espaço para começar ou pausar'}
            </p>
          </div>
        ) : <p className="mt-3 text-center text-xs text-foreground-500">Sem pressa. Use “Próxima nota” ou a seta → quando terminar.</p>}
        <div className="mt-4 flex flex-wrap justify-center gap-2">
          {advance === 'automatic' && <Button icon={running ? 'ri-pause-line' : 'ri-play-line'} onClick={toggleRunning}>{running ? 'Pausar' : 'Começar'}</Button>}
          <Button variant="outline" icon="ri-arrow-right-line" onClick={nextNote}>Próxima nota</Button>
          <Button variant="outline" icon="ri-volume-up-line" onClick={hear}>Ouvir nota</Button>
          <Button variant="ghost" icon="ri-eye-line" onClick={() => setRevealed(true)}>Conferir nota</Button>
        </div>
        {showKeyboard && (
          <div className="mt-5 border-t border-background-200 pt-4">
            <p className="mb-2 text-center text-xs text-foreground-500">Teclado de apoio · toque no seu instrumento</p>
            <PianoKeyboard startMidi={keyboardStart} endMidi={keyboardEnd} disabled silent showNoteNames={showNames || revealed} highlightMidis={showNames || revealed ? [note.midi] : []} />
          </div>
        )}
      </div>

      <div className="mt-5 rounded-xl border border-background-200 bg-background-50 p-4 sm:p-5">
        <h2 className="font-heading text-lg font-bold text-foreground-950">Ajuste o seu treino</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="text-sm font-medium text-foreground-700">Região do teclado
            <select className={fieldClass} value={region} onChange={event => {
              const value = event.target.value;
              setRegion(value);
              reset(value === 'lesson' ? lessonNotes : LETTERS.map(letter => letter + value));
            }}>
              <option value="lesson">Notas desta aula</option>
              <option value="3">3ª oitava · Dó3 a Si3</option>
              <option value="4">4ª oitava · Dó4 a Si4</option>
              <option value="5">5ª oitava · Dó5 a Si5</option>
            </select>
          </label>
          <label className="text-sm font-medium text-foreground-700">Ordem das notas
            <select className={fieldClass} value={order} onChange={event => {
              const value = event.target.value as Order;
              setOrder(value);
              reset(selected, value);
            }}>
              <option value="ascending">Subindo</option><option value="descending">Descendo</option><option value="random">Misturadas</option>
            </select>
          </label>
          <label className="text-sm font-medium text-foreground-700">Avanço das notas
            <select className={fieldClass} value={advance} onChange={event => {
              pause(); setElapsed(0); setAdvance(event.target.value as Advance);
            }}>
              <option value="automatic">Automático</option><option value="manual">No meu ritmo</option>
            </select>
          </label>
          <label className="text-sm font-medium text-foreground-700">Tempo por nota
            <select className={fieldClass} value={seconds} disabled={advance === 'manual'} onChange={event => { pause(); setElapsed(0); setSeconds(Number(event.target.value)); }}>
              {[8,6,4,3,2,1].map(value => <option key={value} value={value}>{value} {value === 1 ? 'segundo' : 'segundos'}</option>)}
            </select>
          </label>
        </div>

        <fieldset className="mt-4">
          <legend className="text-sm font-medium text-foreground-700">Quais notas praticar?</legend>
          <p className="mt-1 text-xs text-foreground-500">Comece com três notas próximas. Acrescente outras aos poucos.</p>
          <div className="mt-2 flex flex-wrap gap-2">
            {available.map(id => (
              <button key={id} type="button" aria-pressed={selected.includes(id)} className={'rounded-md border px-3 py-1.5 text-sm ' + (selected.includes(id) ? 'border-primary-500 bg-primary-100 text-primary-800' : 'border-background-300 text-foreground-600')} onClick={() => {
                if (selected.length === 1 && selected.includes(id)) return;
                reset(selected.includes(id) ? selected.filter(value => value !== id) : sortNotes([...selected, id]));
              }}>{getNote(id).label}</button>
            ))}
            <Button variant="ghost" size="sm" onClick={() => reset(available.slice(0,3))}>Primeiras 3</Button>
            <Button variant="ghost" size="sm" onClick={() => reset(available)}>Todas</Button>
          </div>
        </fieldset>

        <div className="mt-4 grid items-end gap-4 sm:grid-cols-2">
          <label className="text-sm font-medium text-foreground-700">Som de referência
            <select className={fieldClass} value={reference} onChange={event => { pause(); setReference(event.target.value as Reference); }}>
              <option value="after">Depois de tentar</option><option value="before">Ao mostrar a nota</option><option value="off">Desligado</option>
            </select>
          </label>
          <label className="text-sm font-medium text-foreground-700">Volume da referência · {volume}%
            <input className="mt-3 block w-full accent-primary-500" aria-label="Volume da referência" type="range" min="0" max="100" step="5" value={volume} onChange={event => { pause(); setVolume(Number(event.target.value)); }} />
          </label>
        </div>
        <p className="mt-2 text-xs text-foreground-500">
          {advance === 'manual' ? 'No seu ritmo, use “Ouvir nota” depois de tocar.' : 'Em “Depois de tentar”, a referência toca após 60% do tempo da nota.'} Ajuste o volume para ouvir seu instrumento e comparar.
        </p>
        <div className="mt-4 flex flex-wrap gap-x-6 gap-y-3 text-sm text-foreground-700">
          <label className="flex items-center gap-2"><input type="checkbox" className="accent-primary-500" checked={showNames} onChange={event => { setShowNames(event.target.checked); setRevealed(false); }} />Mostrar nome da nota</label>
          <label className="flex items-center gap-2"><input type="checkbox" className="accent-primary-500" checked={showKeyboard} onChange={event => setShowKeyboard(event.target.checked)} />Mostrar teclado de apoio</label>
        </div>
      </div>
      <aside className="mt-5 rounded-lg bg-background-100 p-4 text-sm text-foreground-700">
        <p className="font-semibold text-foreground-900">Pratique a ligação entre pauta e tecla</p>
        <p className="mt-1">Faça uma sequência curta subindo e descendo. Depois misture as mesmas notas e esconda o nome. Só reduza o tempo quando conseguir encontrar as teclas com tranquilidade. O dedo usado pode mudar conforme a música; a nota indica a tecla.</p>
        <p className="mt-3 text-xs text-foreground-500">Sem avaliação automática: este treino não escuta o piano nem recebe MIDI. Você confere a execução. As notas apresentadas não contam como acertos, XP ou domínio da aula.</p>
      </aside>
    </section>
  );
}
